import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { Zap, TrendingUp, Shield, BarChart, Activity, Globe, CheckCircle, Play, Lock, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../utils/api';

const services = [
  { title: 'Wealth Management', desc: 'Expert stock market strategies for long-term wealth creation.', points: ['Personalized plans', 'Risk management', 'Portfolio review'], icon: TrendingUp, color: 'var(--color-accent)' },
  { title: 'High-Return Strategies', desc: 'Active trading and investing to maximize your profits.', points: ['Opportunity-driven investing', 'Active management', 'Profit-focused'], icon: Zap, color: 'var(--color-blue)' },
  { title: 'Research & Analysis', desc: 'Deep market research for informed investment decisions.', points: ['Market reports', 'Stock analysis', 'Economic insights'], icon: BarChart, color: '#9726dc' },
  { title: 'Insurance Services', desc: 'Comprehensive coverage for vehicles, health, and life.', points: ['Vehicle insurance', 'Health plans', 'Term insurance'], icon: Shield, color: '#ef4444' },
  { title: 'Mutual Funds', desc: 'Diversified investment options designed to balance risk and maximize long-term returns.', points: ['Professionally managed portfolios', 'Diversification across assets', 'Growth and income options'], icon: Activity, color: '#d97706' },
  { title: 'Bonds & Fixed Income', desc: 'Stable investment options offering predictable returns and lower risk.', points: ['Fixed income strategies', 'Capital preservation', 'Steady returns'], icon: Globe, color: '#051912' },
];

// VideoCard Component
function VideoCard({ video, onPlay, onSubscribe }: { video: any; onPlay: (video: any) => void; onSubscribe: (video: any) => void }) {
  const { user } = useAuth();
  const isAccessible = video.isSubscribed && !video.isExpired;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="card group cursor-pointer overflow-hidden"
      onClick={() => isAccessible && onPlay(video)}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
        {video.thumbnail_url ? (
          <>
            <img
              src={`${import.meta.env.VITE_API_URL || ''}${video.thumbnail_url}`}
              alt={video.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black/20 transition-opacity duration-300 group-hover:bg-black/30" />
            {isAccessible ? (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-xl transform group-hover:scale-110 transition-transform" style={{ background: 'var(--color-accent)' }}>
                  <Play className="w-6 h-6 text-white ml-1 fill-current" />
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white/60" />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            <Play className="w-12 h-12 text-white/40" />
          </div>
        )}
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-full text-[9px] font-bold text-white uppercase tracking-widest">
          {video.category}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 sm:p-6" onClick={e => isAccessible && e.stopPropagation()}>
        <h3 className="text-base font-bold mb-2 text-[var(--color-text)]">{video.title}</h3>
        <p className="text-xs text-[var(--color-muted)] mb-5 line-clamp-2 leading-relaxed">{video.description}</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-[var(--color-muted)]">Access</p>
            <p className="text-xl font-black" style={{ color: 'var(--color-accent)' }}>₹{video.price}</p>
          </div>
          {isAccessible ? (
            <div className="text-right">
              <div className="flex items-center gap-1 text-xs font-bold" style={{ color: 'var(--color-accent)' }}>
                <CheckCircle className="w-3 h-3" /> Purchased
              </div>
              <p className="text-[10px] text-[var(--color-muted)] mt-0.5">Until {new Date(video.expiryDate).toLocaleDateString()}</p>
            </div>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); onSubscribe(video); }} className="btn btn-primary btn-sm">Buy Now</button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// VideoModal Component
function VideoModal({ video, onClose }: { video: any; onClose: () => void }) {
  const [yid, setYid] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get(`/videos/${video.id}/play`)
      .then((res: any) => setYid(res.youtube_id || ''))
      .catch(() => setError('Unable to load video. Please check your subscription.'))
      .finally(() => setLoading(false));
  }, [video.id]);

  useEffect(() => {
    if (yid && containerRef.current) {
      containerRef.current.requestFullscreen?.().catch((err: any) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    }
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => { });
      }
    };
  }, [yid]);

  if (loading) return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
      <div className="w-10 h-10 rounded-full border-4 border-white border-t-transparent animate-spin" />
    </div>
  );

  if (error || !yid) return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
      <div className="text-white text-center">
        <p className="text-lg font-bold mb-4">{error || 'Video not available.'}</p>
        <button onClick={onClose} className="btn btn-primary px-8">Close</button>
      </div>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl select-none">
      <div ref={containerRef} className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10 group">
        <button onClick={onClose} className="absolute top-4 right-4 z-40 p-2 rounded-full bg-black/50 hover:bg-white/10 text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        <iframe
          src={`https://www.youtube-nocookie.com/embed/${yid}?rel=0&autoplay=1&modestbranding=1&disablekb=1&showinfo=0&iv_load_policy=3&controls=1&origin=${window.location.origin}`}
          title={video.title}
          className="w-full h-full border-0 relative z-10"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />

        <div className="absolute inset-0 z-20 pointer-events-none">
          <div className="absolute bottom-0 right-0 w-24 h-16 pointer-events-auto cursor-default" />
          <div className="absolute top-0 left-0 right-0 h-16 pointer-events-auto cursor-default" />
        </div>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.18], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.18], [1, 0.92]);

  const [currentSlide, setCurrentSlide] = useState(0);

  // Video-related state
  const [videos, setVideos] = useState<any[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<any>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch latest 3 videos
  const fetchLatestVideos = async () => {
    setVideosLoading(true);
    try {
      const endpoint = user ? '/videos/user?page=1&limit=3' : '/videos?page=1&limit=3';
      const data = await api.get(endpoint);
      setVideos(data.data || []);
    } catch (error) {
      console.error('Error fetching latest videos:', error);
      // No sample videos here, as they might confuse the user if it's a real error
      setVideos([]);
    } finally {
      setVideosLoading(false);
    }
  };

  // Handle video subscription/payment
  const handleSubscribe = async (video: any) => {
    if (!user) return void (window.location.href = '/login');
    try {
      // Load Razorpay script dynamically if not already loaded
      if (!(window as any).Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      let order: any;
      try { order = await api.post('/payments/create-order', { video_id: video.id }); }
      catch { toast('Could not initiate payment. Try again.', 'error'); return; }

      const rzp = new (window as any).Razorpay({
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        name: 'Stone Bench Investments',
        description: order.name,
        order_id: order.id,
        prefill: order.prefill,
        theme: { color: '#5f9c4c' },
        handler: async (resp: any) => {
          const verifyRes = { ok: false };
          try { await api.post('/payments/verify', { ...resp, video_id: video.id }); verifyRes.ok = true; } catch { }
          if (verifyRes.ok) {
            toast('Payment successful! Access granted.', 'success');
            fetchLatestVideos();
          } else {
            toast('Payment verification failed. Contact support if amount was deducted.', 'error');
          }
        },
        modal: { ondismiss: () => toast('Payment cancelled.', 'info') },
      });
      rzp.open();
    } catch { toast('Payment initialization failed. Please try again.', 'error'); }
  };

  // Fetch videos on component mount and when user state changes
  useEffect(() => { fetchLatestVideos(); }, [user]);


  // Carousel slides data
  const slides = [
    {
      id: 1,
      backgroundImage: "/slider_background1.jpg"
    },
    {
      id: 2,
      backgroundImage: "/slider_background2.jpg"
    },
    {
      id: 3,
      backgroundImage: "/slider_background3.jpg"
    },
    {
      id: 4,
      backgroundImage: "/slider_background4.jpg"
    },
  ];

  // Carousel navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  // Auto-play carousel
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Magnetic button effect
  useEffect(() => {
    const magneticButtons = document.querySelectorAll('.magnetic-button');

    magneticButtons.forEach(button => {
      const handleMouseMove = (e: MouseEvent) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;

        target.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px) scale(1.05)`;
      };

      const handleMouseLeave = () => {
        (button as HTMLElement).style.transform = 'translate(0px, 0px) scale(1)';
      };

      (button as HTMLElement).addEventListener('mousemove', handleMouseMove as EventListener);
      (button as HTMLElement).addEventListener('mouseleave', handleMouseLeave as EventListener);

      return () => {
        (button as HTMLElement).removeEventListener('mousemove', handleMouseMove as EventListener);
        (button as HTMLElement).removeEventListener('mouseleave', handleMouseLeave as EventListener);
      };
    });
  }, []); // Empty dependency array means run only once on mount

  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.12,
      },
    },
  };

  return (
    <div className="w-full overflow-x-hidden bg-white">
      {/* Hero Slider - Premium Animated */}
      <section className="relative min-h-[92vh] overflow-hidden bg-gray-900">
        <div className="relative w-full h-full">
          {/* Animated Background Images */}
          <div className="relative w-full h-[92vh] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentSlide}
                src={slides[currentSlide].backgroundImage}
                alt={`Slide ${currentSlide + 1}`}
                className="absolute w-full h-full object-cover inset-0"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{
                  duration: 1.5,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                onError={(e) => console.log("Image failed to load:", e)}
                onLoad={() => console.log("Image loaded successfully")}
              />
              <div className="absolute inset-0 bg-black/20 pointer-events-none" />
            </AnimatePresence>

            {/* Overlay Gradient for Better Text Visibility */}
            <div className="absolute inset-0 z-5 bg-gradient-to-b from-black/55 via-black/40 to-black/65" />

            {/* Animated Particles/Effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.1, 0.3],
                  rotate: [0, 180, 360]
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute top-20 right-20 w-40 h-40 md:w-96 md:h-96 bg-gradient-to-r from-[var(--color-accent)]/10 to-[var(--color-blue)]/10 rounded-full blur-3xl"
              />
              <motion.div
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.2, 0.05, 0.2],
                  rotate: [0, -180, -360]
                }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute bottom-20 left-20 w-32 h-32 md:w-80 md:h-80 bg-gradient-to-r from-[#e1ff01]/10 to-[#0d6dd8]/10 rounded-full blur-3xl"
              />
            </div>
            <div className="relative top-[3rem] max-w-5xl z-10 text-center text-white px-4 sm:px-6 md:px-8 mx-auto flex flex-col items-center gap-4 sm:gap-6 w-full py-6 sm:py-8 md:py-12">
              <div className="opacity-0 animate-slide-down-fade" style={{ animationDelay: '0.2s' }}>
                <span className="trusted-text inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(95,156,76,0.1)] border border-[#eee] text-[#fff] text-xs font-bold tracking-wide">
                  <Zap className="w-3 h-3" /> TRUSTED FINANCIAL PARTNER
                </span>
              </div>
              <h1 className="text-[clamp(1.4rem,5vw,2.25rem)] font-black tracking-[-0.03em] leading-[1.1]">
                <motion.span
                  className="inline-block"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1.2, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <span className="bg-gradient-to-r from-white via-[#FFE8A3] to-[#FBCF3B] bg-clip-text text-transparent">
                    "If you truly care about your financial future but don't know where to start,
                  </span>
                </motion.span>
                <motion.span
                  className="inline-block"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 1.2, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <span className="bg-gradient-to-r from-white via-[#FFE8A3] to-[#FBCF3B] bg-clip-text text-transparent">
                    what you need isn't more information
                  </span>
                  <span className="text-white/80">—</span>
                  <span className="text-[#a8e063] font-black">it's the right guidance.</span>"
                </motion.span>
              </h1>
              <div className="community-stats w-full">
                <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
                  {[
                    { label: 'Happy Clients', value: '1000+' },
                    { label: 'Experience', value: '10+' },
                    { label: 'Years of Trust', value: '8+' },
                    { label: 'Community', value: 'Growing' }
                  ].map((stat, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.8,
                        delay: 1.2 + (i * 0.1),
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                      whileHover={{ y: -4 }}
                      className="glass p-3 sm:p-4 md:p-5 rounded-2xl text-center card-hover-glow"
                    >
                      <p className="text-[clamp(1.1rem,3vw,1.8rem)] font-black text-[#fff] mb-0.5">{stat.value}</p>
                      <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-[#fff]">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="community-btn flex flex-wrap gap-3 sm:gap-4 justify-center">
                <Link
                  to="/services"
                  className="animated-border-button btn btn-primary px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base rounded-2xl shadow-[0_0_24px_rgba(95,156,76,0.3)] hover:shadow-[0_0_32px_rgba(95,156,76,0.4)] transform hover:scale-105 transition-all duration-300 opacity-0 animate-slide-up-fade relative z-10"
                  style={{ animationDelay: '0.8s' }}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  Explore Courses
                </Link>
                <Link
                  to="/about"
                  className="animated-border-button-blue btn px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base rounded-2xl bg-[#fff] text-[#5f9c4c] border border-white/20 hover:bg-[#fff] hover:border-white/30 transform hover:scale-105 transition-all duration-300 opacity-0 animate-slide-up-fade relative z-10"
                  style={{ animationDelay: '0.8s' }}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  About Stone Bench
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Services */}
      <section className="section-py container-fluid">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[clamp(1.6rem,3.5vw,2.6rem)] font-black mb-3 text-[var(--color-text)] tracking-tight"
          >
            <motion.span
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="inline-block"
            >
              Our Core
            </motion.span>&nbsp;
            <motion.span
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="inline-block"
              style={{ color: 'var(--color-accent)' }}
            >
              Expertise
            </motion.span>
          </motion.h2>
          <p className="text-[var(--color-muted)] max-w-xl mx-auto text-sm sm:text-base">Comprehensive financial solutions tailored to your goals and risk appetite.</p>
        </div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12 sm:gap-x-7 sm:gap-y-14 mt-6">
          {services.map((s, i) => {
            const Icon = s.icon; return (
              <motion.div key={i} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="relative pt-7 group">
                {/* Icon badge — centered at top, overlapping the card edge */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-500 group-hover:[transform:rotateY(360deg)]"
                    style={{ background: s.color }}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                {/* Card body */}
                <div className="glass rounded-2xl pt-12 pb-6 px-6 sm:px-8 sm:pb-8 text-center hover:border-[rgba(95,156,76,0.3)] transition-all card-hover">
                  <h3 className="text-base font-bold mb-2 text-[var(--color-text)]">{s.title}</h3>
                  <p className="text-sm text-[var(--color-muted)] mb-4 leading-relaxed">{s.desc}</p>
                  <ul className="space-y-2 text-left">{s.points.map((p, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.color }} />{p}
                    </li>
                  ))}</ul>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Latest Videos - Premium Section */}
      <section className="section-py !pt-0 container-fluid">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(95,156,76,0.1)] border border-[rgba(95,156,76,0.2)] text-[var(--color-accent)] text-xs font-bold mb-4">
            <Play className="w-3 h-3" /> LATEST VIDEOS
          </div>
          <motion.h2
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[clamp(1.6rem,3.5vw,2.6rem)] font-black mb-3 text-[var(--color-text)] tracking-tight"
          >
            <motion.span
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="inline-block"
            >
              Premium
            </motion.span>&nbsp;
            <motion.span
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="inline-block"
              style={{ color: 'var(--color-accent)' }}
            >
              Trading Content
            </motion.span>
          </motion.h2>
          <p className="text-[var(--color-muted)] max-w-2xl mx-auto leading-relaxed">
            Access exclusive trading strategies, market analysis, and investment insights from our expert team.
          </p>
        </motion.div>

        {/* Animated Background Elements */}
        <div className="relative mb-8">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.05, 0.1]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-10 left-10 w-32 h-32 bg-[var(--color-accent)] rounded-full blur-3xl"
            />
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.08, 0.03, 0.08]
              }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-10 right-10 w-40 h-40 bg-[var(--color-blue)] rounded-full blur-3xl"
            />
          </div>

          {/* Videos Grid */}
          {videosLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
              {Array.from({ length: 3 }).map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="card overflow-hidden"
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 animate-pulse" />
                  <div className="p-5 sm:p-6 space-y-3">
                    <div className="h-4 bg-gray-700 rounded animate-pulse" />
                    <div className="h-3 bg-gray-700 rounded animate-pulse w-3/4" />
                    <div className="flex justify-between items-center">
                      <div className="space-y-1">
                        <div className="h-3 bg-gray-700 rounded animate-pulse w-12" />
                        <div className="h-5 bg-gray-700 rounded animate-pulse w-16" />
                      </div>
                      <div className="h-8 bg-gray-700 rounded animate-pulse w-20" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[var(--glass-bg)] border border-[var(--glass-border)] flex items-center justify-center">
                <Play className="w-8 h-8 text-[var(--color-muted)]" />
              </div>
              <h3 className="text-xl font-bold text-[var(--color-text)] mb-2">No Videos Available</h3>
              <p className="text-[var(--color-muted)]">Check back soon for our latest trading content and market insights.</p>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7"
            >
              {videos.map((video, index) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  onPlay={setActiveVideo}
                  onSubscribe={handleSubscribe}
                />
              ))}
            </motion.div>
          )}

          {/* View All Videos Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-[var(--color-accent)] text-white font-bold hover:bg-[var(--color-accent)]/90 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              View All Videos
              <Play className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <VideoModal
            video={activeVideo}
            onClose={() => setActiveVideo(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
